import { COMPANY_TYPE } from '@idc-forms/schemas'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useDeleteCompanyMutation, useUpdateCompanyMutation } from '../hooks/use-company-mutations'
import type { CompanyAdminRow } from '../models/company-admin'
import {
  CompanyActiveBadge,
  CompanyDefaultBadge,
  CompanyTypeBadge
} from './companies-admin-columns'
import { CompanyForm } from './company-form'

type CompanyDetailPanelProps = {
  company: CompanyAdminRow
}

/**
 * Panel de detalle de la empresa: modo VISTA (resumen + acciones + tab "Más
 * información" con acordeón de actividad) y modo FORMULARIO in-place (mismo
 * CompanyForm que la creación). Eliminar se oculta cuando el backend lo va a
 * rechazar de plano (bloqueada, sistema CVN o predeterminada).
 */
export function CompanyDetailPanel({ company }: CompanyDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateCompanyMutation()
  const deleteMutation = useDeleteCompanyMutation()
  const { has } = usePermissions()

  const isDeletable = !company.blocked && !company.default && company.type !== COMPANY_TYPE.CVN

  const toggleActive = () => {
    updateMutation.mutate({ id: company._id, payload: { active: !company.active } })
  }

  if (mode === 'edit') {
    return (
      <CompanyForm
        defaultValues={{
          name: company.name,
          giinNumber: company.giinNumber ?? undefined,
          default: company.default,
          active: company.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: company._id, payload: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{company.name}</h3>
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<CompanyActiveBadge active={company.active} />} />
            <DetailFieldRow label="Tipo" value={<CompanyTypeBadge type={company.type} />} />
            <DetailFieldRow
              label="GIIN"
              value={company.giinNumber || '—'}
              copyValue={company.giinNumber || undefined}
            />
            <DetailFieldRow
              label="Predeterminada"
              value={company.default ? <CompanyDefaultBadge /> : 'No'}
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateCompanies)
              ? [
                  {
                    label: 'Editar empresa',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: company.active ? 'Desactivar empresa' : 'Activar empresa',
                    icon: company.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteCompanies) && isDeletable
              ? [
                  {
                    label: 'Eliminar empresa',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['activity']}>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(company.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={company.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(company.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={company.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{company.name}”. Las sociedades y agencias que la referencian se
              conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(company._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
